import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'
import type { ReplyTemplate, ReplyTemplateMeta, Resource, TicketDetail } from '@/lib/api/types'

export interface ReplyTemplateInput {
  title: string
  body: string
  category: string | null
}

export function useReplyTemplates() {
  return useQuery({
    queryKey: ['reply-templates'],
    queryFn: () => http.get<{ data: ReplyTemplate[]; meta: ReplyTemplateMeta }>('/reply-templates'),
  })
}

export function useSaveReplyTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: ReplyTemplateInput & { id?: string }) =>
      id
        ? (await http.patch<Resource<ReplyTemplate>>(`/reply-templates/${id}`, input)).data
        : (await http.post<Resource<ReplyTemplate>>('/reply-templates', input)).data,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['reply-templates'] }),
  })
}

export function useDeleteReplyTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => http.delete(`/reply-templates/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['reply-templates'] }),
  })
}

/** Counted so the most used come first; a failure doesn't matter to the reply. */
export function useTemplateUsed() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => http.post(`/reply-templates/${id}/used`),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['reply-templates'] }),
  })
}

/** What each placeholder becomes on this ticket, for this staff member. */
export function placeholderValues(ticket: TicketDetail, staffName: string): Record<string, string> {
  const customer = ticket.requester?.name.trim() ?? ''
  const staff = staffName.trim()
  return {
    customer_first_name: customer.split(/\s+/)[0] || 'there',
    customer_name: customer || 'there',
    workspace: ticket.workspace?.name ?? ticket.requester?.company ?? 'your workspace',
    website: ticket.project?.domain ?? 'your website',
    plan: ticket.plan?.name ?? 'current',
    ticket: ticket.reference,
    staff_first_name: staff.split(/\s+/)[0] ?? '',
    staff_name: staff,
  }
}

/** The template with its placeholders filled in; unknown ones stay as written. */
export function fillTemplate(body: string, values: Record<string, string>) {
  return body.replace(/\{([a-z_]+)\}/g, (match, key: string) => values[key] ?? match)
}

/** Placeholders that would wrongly replace ordinary words, or fallbacks that aren't real values. */
const notReversed = new Set(['plan'])
const fallbacks = new Set(['there', 'your workspace', 'your website', 'current', ''])

/**
 * A reply turned back into a template: this ticket's names and details become placeholders
 * (longest first, whole words only), so it reads right on any ticket.
 */
export function toTemplate(reply: string, given: Record<string, string>) {
  const values = { ...given }
  // A one-word name is the first name.
  if (values.customer_name === values.customer_first_name) delete values.customer_name
  if (values.staff_name === values.staff_first_name) delete values.staff_name
  const all = Object.values(values)
  const pairs = Object.entries(values)
    // A value two placeholders share (the customer and staff are both "Ana") stays as written.
    .filter(([key, value]) => !notReversed.has(key) && !fallbacks.has(value) && value.length >= 2)
    .filter(([, value]) => all.filter((v) => v === value).length === 1)
    .sort((a, b) => b[1].length - a[1].length)
  return pairs.reduce((text, [key, value]) => {
    const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return text.replace(new RegExp(`(?<![\\p{L}\\p{N}_])${escaped}(?![\\p{L}\\p{N}_])`, 'gu'), `{${key}}`)
  }, reply)
}

/** Templates for this ticket first (its category, then any ticket), most used first. */
export function rankTemplates(templates: ReplyTemplate[], category: string | null) {
  const group = (t: ReplyTemplate) =>
    category !== null && t.category === category ? 0 : t.category === null ? 1 : 2
  return [...templates].sort(
    (a, b) => group(a) - group(b) || b.uses - a.uses || a.title.localeCompare(b.title),
  )
}
